class DlPageProductListMultiTabM extends HTMLElement {
  connectedCallback() {
    this.badgeEvents?.abort();
    this.badgeEvents = new AbortController();
    const { signal } = this.badgeEvents;
    this.badgeResizeObserver?.disconnect();
    this.badgeResizeObserver = new ResizeObserver(() => this.fitBlackFridayBadges());
    this.tabs = Array.from(this.querySelectorAll('.dl-multi-tabs-products__tab'));
    this.panels = Array.from(this.querySelectorAll('.dl-multi-tabs-products__panel'));
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.selectTab(tab)));
    this.panels.forEach((panel) => {
      panel.querySelector('.dl-multi-tabs-products__more')?.addEventListener('click', (event) => {
        this.togglePanel(panel, event.currentTarget);
      });
    });
    this.querySelectorAll('dl-product-grid-item-h').forEach((card) => {
      this.initializeCard(card);
    });
    if (this.classList.contains('dl-multi-tabs-products--black-friday')) {
      this.querySelectorAll('.dl-product-grid-item-h-sale').forEach((badge) => this.badgeResizeObserver.observe(badge));
      document.fonts?.addEventListener('loadingdone', () => this.fitBlackFridayBadges(), { signal });
      document.fonts?.ready.then(() => {
        if (!signal.aborted) this.fitBlackFridayBadges();
      });
    }
    this.addEventListener('change', (event) => {
      const card = event.target.closest('dl-product-grid-item-h');
      if (!card || !event.target.matches('.dl-product-grid-item-h-selling-plan-select')) return;
      const selectedValue = event.target.value;
      this.initializeCard(card);
      event.target.value = selectedValue;
      if (!card.multiTabVariant) return;
      if (event.target.matches('.dl-multi-tabs-products__style-select')) {
        const current = card.multiTabVariant;
        const index = Number(event.target.dataset.optionIndex);
        const variant = card.multiTabData.variants.find((item) => item.options.every(
          (value, optionIndex) => value === (optionIndex === index ? event.target.value : current.options[optionIndex])
        ));
        if (variant) this.selectVariant(card, variant);
      } else if (event.target.matches('.dl-product-grid-item-h-selling-plan-select')) {
        this.updateSellingPlan(card);
      }
    }, { capture: true, signal });
    // The shared color handler stops bubbling; capture and reconcile after it runs.
    this.addEventListener('click', (event) => {
      const color = event.target.closest('.dl-product-grid-item-h-colors-item');
      if (!color) return;
      const card = color.closest('dl-product-grid-item-h');
      window.requestAnimationFrame(() => {
        const data = card.multiTabData;
        const colorVariant = data?.variants.find((item) => item.id === color.dataset.dlVariantId);
        if (!colorVariant) return;
        const style = card.querySelector('.dl-multi-tabs-products__style-select');
        const index = Number(style?.dataset.optionIndex);
        const variant = style ? data.variants.find((item) => item.options.every(
          (value, optionIndex) => value === (optionIndex === index ? style.value : colorVariant.options[optionIndex])
        )) : null;
        this.selectVariant(card, variant || colorVariant);
      });
    }, true);
  }

  disconnectedCallback() {
    this.badgeEvents?.abort();
    this.badgeResizeObserver?.disconnect();
  }

  initializeCard(card) {
    if (card.multiTabVariant) return;
    const data = card.querySelector('[data-multi-tab-variants]');
    if (!data) return;
    card.multiTabData = JSON.parse(data.textContent);
    const id = card.querySelector('input[name="id"]')?.value || card.multiTabData.initialVariantId;
    const variant = card.multiTabData.variants.find((item) => item.id === String(id))
      || card.multiTabData.variants[0];
    if (variant) this.selectVariant(card, variant);
  }

  fitBlackFridayBadges(root = this) {
    if (!this.classList.contains('dl-multi-tabs-products--black-friday')) return;
    this.badgeMeasureContext ||= document.createElement('canvas').getContext('2d');
    const context = this.badgeMeasureContext;
    if (!context) return;
    root.querySelectorAll('.dl-multi-tabs-products__badge-text').forEach((text) => {
      // Always measure at the CSS size so shorter discounts can grow back.
      text.style.removeProperty('font-size');
      let amount = text.querySelector('[data-badge-amount]');
      if (!amount) {
        const match = text.textContent.trim().match(/^(.*?)\s+OFF$/i);
        if (!match) return;
        amount = document.createElement('span');
        amount.dataset.badgeAmount = '';
        amount.textContent = match[1];
        text.replaceChildren(amount, document.createTextNode(' OFF'));
      }
      amount.style.removeProperty('font-size');
      const width = amount.clientWidth;
      if (!width) return;
      const style = getComputedStyle(amount);
      context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const spacing = parseFloat(style.letterSpacing) || 0;
      const widest = Math.max(...amount.textContent.trim().split(/\s+/).map((word) => {
        const label = style.textTransform === 'uppercase' ? word.toUpperCase() : word;
        return context.measureText(label).width + Math.max(0, label.length - 1) * spacing;
      }));
      if (widest > width) {
        amount.style.fontSize = `${Math.floor(parseFloat(style.fontSize) * width / widest * 100) / 100}px`;
      }
    });
  }

  selectTab(selectedTab) {
    const index = selectedTab.dataset.tabIndex;
    this.tabs.forEach((tab) => {
      const active = tab === selectedTab;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
    });
    this.panels.forEach((panel) => {
      const active = panel.dataset.panelIndex === index;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
      if (active) {
        panel.querySelectorAll('dl-product-grid-item-h').forEach((card) => {
          this.initializeCard(card);
          this.updateSellingPlan(card);
        });
      }
    });
    if (window.matchMedia('(max-width: 990px)').matches) {
      const tabs = selectedTab.closest('.dl-multi-tabs-products__tabs');
      const padding = parseFloat(getComputedStyle(tabs).paddingLeft) || 0;
      tabs.scrollTo({
        left: tabs.scrollLeft + selectedTab.getBoundingClientRect().left
          - tabs.getBoundingClientRect().left - tabs.clientLeft - padding,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    }
  }

  togglePanel(panel, button) {
    const allShown = button.getAttribute('aria-expanded') === 'true';
    const items = Array.from(panel.querySelectorAll('.dl-multi-tabs-products__item'));
    if (allShown) {
      items.forEach((item) => item.classList.remove('is-revealed'));
      button.setAttribute('aria-expanded', 'false');
      button.classList.remove('dl-all');
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const hiddenItems = items.filter((item) => window.getComputedStyle(item).display === 'none');
    const revealCount = Number(window.innerWidth <= 990 ? this.dataset.mobileRevealCount : this.dataset.desktopRevealCount) || 6;
    hiddenItems.slice(0, revealCount).forEach((item) => item.classList.add('is-revealed'));
    const hasHiddenItems = items.some((item) => window.getComputedStyle(item).display === 'none');
    button.setAttribute('aria-expanded', String(!hasHiddenItems));
    button.classList.toggle('dl-all', !hasHiddenItems);
  }

  selectVariant(card, variant) {
    const initial = !card.multiTabVariant;
    card.multiTabVariant = variant;
    const style = card.querySelector('.dl-multi-tabs-products__style-select');
    if (style) {
      const index = Number(style.dataset.optionIndex);
      style.value = variant.options[index];
      Array.from(style.options).forEach((option) => {
        option.disabled = !card.multiTabData.variants.some((item) => item.options.every(
          (value, optionIndex) => value === (optionIndex === index ? option.value : variant.options[optionIndex])
        ));
      });
    }
    card.querySelectorAll('input[name="id"]').forEach((input) => {
      input.value = variant.id;
      input.dataset.variantSku = variant.sku || '';
      input.disabled = !variant.available;
    });
    card.querySelectorAll('input[name="quantity"]').forEach((input) => { input.disabled = !variant.available; });
    card.querySelectorAll('button[type="submit"]').forEach((button) => {
      button.disabled = !variant.available;
      const label = button.querySelector('span');
      if (label) label.textContent = variant.available ? button.dataset.addToCartText : button.dataset.soldOutText;
    });
    const imageContainer = card.querySelector('.dl-product-grid-item-h-image');
    if (imageContainer && card.multiTabData.useBlackFridayImage) {
      const image = imageContainer.querySelector('picture img');
      if (image && variant.image) {
        image.removeAttribute('srcset');
        image.src = variant.image;
        image.classList.add('dl-selected');
        const source = image.closest('picture').querySelector('source');
        if (source) source.srcset = variant.mobileImage || variant.image;
      }
    } else if (imageContainer && variant.image) {
      let image = imageContainer.querySelector('.dl-multi-tabs-products__variant-image');
      if (!image) {
        image = document.createElement('img');
        image.className = 'dl-multi-tabs-products__variant-image';
        image.alt = imageContainer.querySelector('img')?.alt || '';
        imageContainer.appendChild(image);
      }
      imageContainer.querySelectorAll('img').forEach((item) => item.classList.remove('dl-selected'));
      image.src = variant.image;
      image.classList.add('dl-selected');
    }
    const select = card.querySelector('.dl-product-grid-item-h-selling-plan-select:not(.dl-multi-tabs-products__style-select)');
    if (select) {
      const previous = initial ? card.querySelector('input[name="selling_plan_id"]')?.value : select.value;
      const options = variant.plans.map((plan) => new Option(plan.name, plan.id));
      if (!card.multiTabData.requiresSellingPlan) options.push(new Option(this.dataset.oneTimeText, ''));
      select.replaceChildren(...options);
      select.value = options.some((option) => option.value === previous) ? previous : (variant.plans[0]?.id || '');
      select.disabled = options.length === 0;
    }
    if (card.multiTabData.prioritizeBfDiscount) {
      const badge = card.querySelector('.dl-product-grid-item-h-sale');
      if (badge) {
        badge.textContent = variant.discountLabel || '';
        badge.style.display = variant.discountLabel ? '' : 'none';
      }
    }
    if (this.classList.contains('dl-multi-tabs-products--black-friday')) {
      const badge = card.querySelector('.dl-product-grid-item-h-sale');
      if (badge) {
        const discount = this.formatBlackFridayDiscount(badge.textContent);
        let label = badge.querySelector('.dl-multi-tabs-products__badge-text');
        if (!label) {
          label = document.createElement('span');
          label.className = 'dl-multi-tabs-products__badge-text';
          badge.replaceChildren(label);
        }
        label.textContent = discount;
        if (!discount) badge.style.display = 'none';
        this.fitBlackFridayBadges(card);
      }
    }
    this.updateSellingPlan(card);
  }

  formatBlackFridayDiscount(text) {
    const percentage = text.match(/(\d+(?:[.,]\d+)?)\s*%/);
    if (percentage) return `${percentage[1]}% OFF`;
    const amount = text.match(/(?:(?:\b(?:US|CA|AU|NZ|HK|SG|NT|USD|CAD|AUD)\s*)?[$€£¥]\s*\d+(?:[.,]\d+)*|\d+(?:[.,]\d+)*\s*(?:USD|CAD|AUD|EUR|GBP))\b/i);
    if (amount) return `${amount[0].trim()} OFF`;
    const discount = text.match(/(\d+(?:[.,]\d+)?)\s+off\b/i);
    return discount ? `${discount[1]} OFF` : '';
  }

  updateSellingPlan(card) {
    const variant = card?.multiTabVariant;
    if (!variant) return;
    const select = card.querySelector('.dl-product-grid-item-h-selling-plan-select:not(.dl-multi-tabs-products__style-select)');
    const plan = variant.plans.find((item) => item.id === select?.value);
    const requiresUnavailablePlan = card.multiTabData.requiresSellingPlan && !plan;
    card.querySelectorAll('input[name="selling_plan_id"]').forEach((input) => { input.value = plan?.id || ''; });
    const button = card.querySelector('.dl-product-grid-item-h-add-to-cart > dl-product-form-h button[type="submit"]');
    if (button) {
      const text = plan ? this.dataset.subscribeText : this.dataset.addToCartText;
      button.dataset.addToCartText = text;
      button.disabled = !variant.available || requiresUnavailablePlan;
      button.querySelector('span').textContent = button.disabled ? button.dataset.soldOutText : text;
    }
    // Ordinary cards keep their existing coupon, gift-card and custom price logic.
    const hasBfOverride = card.multiTabData.prioritizeBfDiscount && variant.bfDiscount != null;
    if (!select && !card.querySelector('.dl-multi-tabs-products__style-select') && !hasBfOverride) return;
    const price = card.querySelector('.dl-product-grid-item-h-prices-price');
    let compare = card.querySelector('.dl-product-grid-item-h-prices-original');
    const isOneTime = Boolean(select) && !plan;
    const compareText = isOneTime ? ''
      : plan && !hasBfOverride ? (plan.discounted ? variant.basePrice : '') : variant.comparePrice;
    const badge = card.querySelector('.dl-product-grid-item-h-sale');
    if (select && badge) {
      // The badge must follow the same discount state as the displayed price.
      if (card.subscriptionDiscountLabel === undefined) card.subscriptionDiscountLabel = badge.textContent;
      const text = !compareText ? '' : hasBfOverride ? variant.discountLabel
        : plan ? card.subscriptionDiscountLabel : variant.oneTimeDiscountLabel;
      const label = badge.querySelector('.dl-multi-tabs-products__badge-text') || badge;
      label.textContent = text || '';
      badge.hidden = !text;
      badge.style.display = text ? '' : 'none';
      this.fitBlackFridayBadges(card);
    }
    if (!compare && compareText) {
      compare = document.createElement('div');
      compare.className = 'dl-product-grid-item-h-prices-original';
      price?.parentElement.appendChild(compare);
    }
    if (price) {
      price.textContent = isOneTime ? variant.basePrice
        : plan && !hasBfOverride ? plan.price : variant.price;
      price.classList.toggle('dl-product-grid-item-h-prices-price-sale', Boolean(compareText));
    }
    if (compare) {
      compare.textContent = compareText || '';
      compare.style.display = compareText ? '' : 'none';
    }
  }
}

if (!customElements.get('dl-page-product-list-multi-tab-m')) {
  customElements.define('dl-page-product-list-multi-tab-m', DlPageProductListMultiTabM);
}
